import { z } from "zod"

/**
 * Fly.trade Quote API Response Schema
 * Based on actual API response from https://api.fly.trade/aggregator/quote
 * Updated to match current Fly.trade API (rebranded from Magpie)
 */
export const flyQuoteResponseSchema = z.object({
  // Required fields
  id: z.string(), // Quote identifier
  amountOut: z.string(), // Output amount in wei (as string)
  targetAddress: z.string(), // Address for token approval/transaction target

  // Optional fields
  fees: z
    .array(
      z.object({
        type: z.string(), // e.g., "gas"
        value: z.string(), // e.g., "0.0008"
      }),
    )
    .optional(),

  resourceEstimate: z
    .object({
      gasLimit: z.string(), // Gas estimate as string (e.g., "266068")
    })
    .optional(),

  typedData: z
    .object({
      types: z.object({
        Swap: z.array(
          z.object({
            name: z.string(),
            type: z.string(),
          }),
        ),
      }),
      domain: z.object({
        name: z.string(), // e.g., "Magpie Router"
        version: z.string(), // e.g., "3"
        chainId: z.string(), // e.g., "8453"
        verifyingContract: z.string(), // Contract address
      }),
      message: z.object({
        router: z.string(),
        sender: z.string(),
        recipient: z.string(),
        fromAsset: z.string(),
        toAsset: z.string(),
        deadline: z.string(),
        amountOutMin: z.string(),
        swapFee: z.string().optional(),
        amountIn: z.string().optional(),
      }).passthrough(),
    })
    .passthrough()
    .optional(),

  // Legacy fields for backward compatibility (if needed)
  "quote-id": z.string().optional(),
  toTokenAmount: z.string().optional(),
  message: z.any().optional(), // Old format, kept for compatibility
})

export type FlyQuoteResponse = z.infer<typeof flyQuoteResponseSchema>

/**
 * Fly Transaction API Response Schema
 * Based on Fly API documentation
 *
 * Note: This schema is incomplete and needs to be updated with actual API response examples.
 */
export const flyTransactionResponseSchema = z.object({
  to: z.string(),
  data: z.string(),
  value: z.string().optional(),
  gas: z.string().optional(),
  gasLimit: z.string().optional(),
  // Additional fields TBD
})

export type FlyTransactionResponse = z.infer<typeof flyTransactionResponseSchema>
